import { useEffect, useMemo, useState } from 'react';
import { Info, RotateCcw, Save } from 'lucide-react';
import Layout from '../../components/Layout';
import { useTranslation } from '../../i18n/useTranslation';
import { useAlert } from '../../contexts/AlertContext';
import {
  settingsService,
  SettingType,
  type SettingGroupDto,
  type SettingItemDto,
} from '../../api/settingsService';

type ValueMap = Record<string, string>;

function inputTypeFor(type: SettingType): string {
  switch (type) {
    case SettingType.Number:
      return 'number';
    case SettingType.Email:
      return 'email';
    case SettingType.Url:
      return 'url';
    default:
      return 'text';
  }
}

function buildValueMap(groups: SettingGroupDto[]): ValueMap {
  const map: ValueMap = {};
  for (const group of groups) {
    for (const s of group.settings) {
      map[s.name] = s.value ?? '';
    }
  }
  return map;
}

export default function SettingsManagementPage() {
  const { t } = useTranslation();
  const { showToast } = useAlert();
  const [groups, setGroups] = useState<SettingGroupDto[]>([]);
  const [values, setValues] = useState<ValueMap>({});
  const [originalValues, setOriginalValues] = useState<ValueMap>({});
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadSettings = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await settingsService.getAll();
      setGroups(data);
      const map = buildValueMap(data);
      setValues(map);
      setOriginalValues(map);
    } catch (err) {
      const message =
        (err as { userMessage?: string })?.userMessage ?? t('App.Admin.Settings.SettingsManagementPage.LoadError');
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const isDirty = useMemo(() => {
    return Object.keys(values).some((k) => values[k] !== (originalValues[k] ?? ''));
  }, [values, originalValues]);

  const handleChange = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    const reset: ValueMap = { ...values };
    for (const group of groups) {
      for (const s of group.settings) {
        reset[s.name] = s.defaultValue ?? '';
      }
    }
    setValues(reset);
    showToast(t('App.Admin.Settings.SettingsManagementPage.ResetToast'), { type: 'info' });
  };

  const handleDiscard = () => {
    setValues(originalValues);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        settings: Object.keys(values).map((name) => ({
          name,
          value: values[name] ?? '',
        })),
      };
      await settingsService.update(payload);
      setOriginalValues(values);
      showToast(t('App.Admin.Settings.SettingsManagementPage.SaveSuccess'), { type: 'success' });
    } catch (err) {
      const message =
        (err as { userMessage?: string })?.userMessage ?? t('App.Admin.Settings.SettingsManagementPage.SaveError');
      showToast(message, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const activeGroup = groups[activeTab];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h1 className="text-2xl font-semibold text-neutral-900">{t('App.Admin.Settings.SettingsManagementPage.Title')}</h1>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleReset}
              disabled={loading || saving || groups.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm rounded border border-neutral-300 text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            >
              <RotateCcw className="w-4 h-4" />
              {t('App.Admin.Settings.SettingsManagementPage.ResetToDefaults')}
            </button>
            <button
              type="button"
              onClick={handleDiscard}
              disabled={!isDirty || saving}
              className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm rounded border border-neutral-300 text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            >
              {t('App.Admin.Settings.SettingsManagementPage.DiscardChanges')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? t('App.Admin.Settings.SettingsManagementPage.Saving') : t('App.Admin.Settings.SettingsManagementPage.Save')}
            </button>
          </div>
        </div>

        {loading && <p className="text-neutral-600">{t('App.Admin.Settings.SettingsManagementPage.Loading')}</p>}

        {loadError && !loading && (
          <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {loadError}
            <button
              type="button"
              onClick={loadSettings}
              className="ml-3 underline hover:no-underline"
            >
              {t('App.Admin.Settings.SettingsManagementPage.Retry')}
            </button>
          </div>
        )}

        {!loading && !loadError && groups.length === 0 && (
          <p className="text-neutral-600">{t('App.Admin.Settings.SettingsManagementPage.NoSettings')}</p>
        )}

        {!loading && !loadError && groups.length > 0 && (
          <div className="bg-white rounded-lg border border-neutral-200">
            {groups.length > 1 && (
              <div
                className="flex flex-wrap border-b border-neutral-200"
                role="tablist"
                aria-label={t('App.Admin.Settings.SettingsManagementPage.GroupsAriaLabel')}
              >
                {groups.map((g, idx) => (
                  <button
                    key={g.groupName}
                    role="tab"
                    aria-selected={activeTab === idx}
                    type="button"
                    onClick={() => setActiveTab(idx)}
                    className={`px-4 py-3 text-sm font-medium min-h-[44px] border-b-2 transition-colors ${
                      activeTab === idx
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-neutral-600 hover:text-neutral-900'
                    }`}
                  >
                    {g.displayName || g.groupName}
                  </button>
                ))}
              </div>
            )}

            {activeGroup && (
              <div className="p-4 sm:p-6 divide-y divide-neutral-200">
                {activeGroup.settings.length === 0 ? (
                  <p className="text-neutral-600">{t('App.Admin.Settings.SettingsManagementPage.GroupNoSettings')}</p>
                ) : (
                  activeGroup.settings.map((setting) => (
                    <SettingRow
                      key={setting.name}
                      setting={setting}
                      value={values[setting.name] ?? ''}
                      onChange={(v) => handleChange(setting.name, v)}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

interface SettingRowProps {
  setting: SettingItemDto;
  value: string;
  onChange: (value: string) => void;
}

function SettingRow({ setting, value, onChange }: SettingRowProps) {
  const { t } = useTranslation();
  const inputId = `setting-${setting.name}`;
  const label = setting.displayName || setting.name;
  const description = setting.description;

  return (
    <div className="py-5 first:pt-0 last:pb-0 grid grid-cols-1 md:grid-cols-2 md:gap-6">
      <div className="mb-2 md:mb-0">
        <label htmlFor={inputId} className="block text-sm font-semibold text-neutral-900">
          <span className="inline-flex items-center gap-1.5">
            {label}
            {description && (
              <span title={description} className="text-neutral-400">
                <Info className="w-4 h-4" aria-hidden="true" />
                <span className="sr-only">{description}</span>
              </span>
            )}
          </span>
        </label>
        {description && (
          <p className="mt-1 text-xs text-neutral-500">{description}</p>
        )}
      </div>
      <div>
        {setting.type === SettingType.TextArea ? (
          <textarea
            id={inputId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={6}
            className="w-full rounded border border-neutral-300 bg-neutral-50 focus:bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 px-3 py-2 text-sm font-mono"
          />
        ) : setting.type === SettingType.Boolean ? (
          <label className="inline-flex items-center gap-2 min-h-[44px]">
            <input
              id={inputId}
              type="checkbox"
              checked={value === 'true'}
              onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
              className="w-5 h-5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-neutral-700">{t('App.Admin.Settings.SettingsManagementPage.SettingEnabled')}</span>
          </label>
        ) : (
          <input
            id={inputId}
            type={inputTypeFor(setting.type)}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full rounded border border-neutral-300 bg-neutral-50 focus:bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 px-3 py-2 text-sm min-h-[44px]"
          />
        )}
      </div>
    </div>
  );
}
